/**
 * V38.24/V38.25 — Page publique constructeur : paiement + signature CAP BATI+ CMI.
 *
 * Accès via `/devis-cap-bati/:token` sans login. Le constructeur :
 *   1. Consulte le devis (lignes, total)
 *   2. Choisit son mode de paiement : CB (Stripe) / Virement Qonto / SEPA 3x (GoCardless)
 *   3. Signe le devis par OTP (SMS Twilio Verify OU Email Brevo)
 *
 * Workflow mutualisé : paiement initié → signature autorisée → devis `signed`.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CreditCard, Landmark, Repeat, Mail, MessageSquare, ShieldCheck,
  CheckCircle2, Clock, Loader2, FileText, AlertTriangle, Copy,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ccmi-cap-bati/cap-bati-plus-cmi`;

const fmtEUR = (n) => Number(n || 0).toLocaleString('fr-FR', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
});

const paymentStatusBadge = (s) => {
  const map = {
    none: { label: 'À régler', color: 'bg-slate-200 text-slate-700' },
    pending: { label: 'En attente', color: 'bg-amber-100 text-amber-800' },
    partial_paid: { label: 'Partiellement payé', color: 'bg-blue-100 text-blue-800' },
    paid: { label: 'Payé', color: 'bg-emerald-100 text-emerald-800' },
    failed: { label: 'Échec', color: 'bg-red-100 text-red-800' },
  };
  const v = map[s] || map.none;
  return <Badge className={v.color}>{v.label}</Badge>;
};

const signatureStatusBadge = (s) => {
  const map = {
    none: { label: 'Non signé', color: 'bg-slate-200 text-slate-700' },
    otp_sent: { label: 'Code envoyé', color: 'bg-amber-100 text-amber-800' },
    signed: { label: 'Signé', color: 'bg-emerald-100 text-emerald-800' },
  };
  const v = map[s] || map.none;
  return <Badge className={v.color}>{v.label}</Badge>;
};

export default function CapBatiPublicQuotePage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  // Paiement UI state
  const [initing, setInitingPayment] = useState(false);
  const [virementInfo, setVirementInfo] = useState(null);
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');

  // Signature UI state
  const [otpChannel, setOtpChannel] = useState('email');
  const [otpContact, setOtpContact] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpRequestedAt, setOtpExpiresAt] = useState(null);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);

  const fetchQuote = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/quote/${token}`);
      setQuote(r.data);
      if (r.data?.constructor_email && !customerEmail) {
        setCustomerEmail(r.data.constructor_email);
      }
      if (r.data?.signature_status === 'otp_sent') {
        setOtpRequested(true);
        setOtpChannel(r.data.signature_channel || 'email');
      }
      setErrorMsg(null);
    } catch (e) {
      setErrorMsg(e?.response?.status === 404
        ? 'Devis introuvable (lien invalide ou expiré).'
        : 'Erreur de chargement du devis.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/quote/${token}`);
        if (cancelled) return;
        setQuote(r.data);
        if (r.data?.constructor_email) setCustomerEmail(r.data.constructor_email);
        if (r.data?.signature_status === 'otp_sent') {
          setOtpRequested(true);
          setOtpChannel(r.data.signature_channel || 'email');
        }
      } catch (e) {
        if (cancelled) return;
        setErrorMsg(e?.response?.status === 404
          ? 'Devis introuvable (lien invalide ou expiré).'
          : 'Erreur de chargement du devis.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Retour Stripe : ?paid=1&session_id=...
  useEffect(() => {
    if (searchParams.get('paid') === '1') {
      toast.success('Paiement en cours de validation par Stripe. Le statut sera mis à jour sous 1 min.');
      // Refresh dans 3s
      setTimeout(() => fetchQuote(), 3000);
    } else if (searchParams.get('cancelled') === '1') {
      toast.error('Paiement annulé.');
    } else if (searchParams.get('sepa_signed') === '1') {
      // Callback GoCardless : compléter le mandat + créer les 3 paiements
      (async () => {
        try {
          await axios.post(`${API}/quotes/${quote?.id || ''}/payment/complete-sepa-3x`);
          toast.success('Mandat SEPA signé. 3 prélèvements programmés (40/30/30).');
          await fetchQuote();
        } catch (e) {
          toast.error('Erreur finalisation SEPA : ' + (e?.response?.data?.detail || e.message));
        }
      })();
    }
  }, [quote?.id, searchParams]);

  const initPayment = async (method) => {
    if (!quote) return;
    if (method === 'sepa_3x' && !customerEmail) {
      toast.error('Email requis pour le mandat SEPA');
      return;
    }
    setInitingPayment(true);
    setVirementInfo(null);
    try {
      const origin = window.location.origin;
      const r = await axios.post(`${API}/quotes/${quote.id}/payment/init`, {
        method,
        customer_email: customerEmail || undefined,
        customer_name: customerName || undefined,
        return_url: origin,
      });
      if (method === 'cb' || method === 'sepa_3x') {
        // Redirection vers Stripe Checkout / GoCardless Redirect Flow
        toast.info('Redirection vers le portail de paiement…');
        window.location.assign(r.data.payment_url);
      } else if (method === 'virement') {
        setVirementInfo(r.data);
        toast.success('Référence virement générée.');
        await fetchQuote();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec init paiement');
    } finally {
      setInitingPayment(false);
    }
  };

  const requestOtp = async () => {
    if (!quote || !otpContact) {
      toast.error('Renseignez votre contact');
      return;
    }
    setOtpSending(true);
    try {
      const r = await axios.post(`${API}/quotes/${quote.id}/signature/request-otp`, {
        channel: otpChannel,
        contact: otpContact,
      });
      setOtpRequested(true);
      setOtpExpiresAt(r.data.expires_at);
      toast.success(`Code envoyé (${otpChannel === 'sms' ? 'SMS' : 'Email'}).`);
      await fetchQuote();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec envoi OTP');
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtp = async () => {
    if (!quote || !otpCode) return;
    setOtpVerifying(true);
    try {
      await axios.post(`${API}/quotes/${quote.id}/signature/verify-otp`, {
        code: otpCode.trim(),
      });
      toast.success('Devis signé avec succès !');
      setOtpCode('');
      await fetchQuote();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Code invalide');
    } finally {
      setOtpVerifying(false);
    }
  };

  const copyText = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copié');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md border-red-200">
          <CardContent className="pt-8 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-3" />
            <p className="text-slate-700">{errorMsg}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!quote) return null;

  const isPaid = quote.payment_status === 'paid';
  const paymentInitiated = ['pending', 'partial_paid', 'paid'].includes(quote.payment_status);
  const isSigned = quote.signature_status === 'signed';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4" data-testid="cap-bati-public-page">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800">
            Devis CAP BATI+ CMI
          </h1>
          <p className="text-sm text-slate-500 mt-1 font-mono">
            {quote.reference}
          </p>
          {quote.project_reference && (
            <p className="text-xs text-slate-400 mt-1">
              Projet CCMI : <span className="font-mono">{quote.project_reference}</span>
            </p>
          )}
        </div>

        {/* Résumé devis */}
        <Card data-testid="quote-summary-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-700" /> Détail du devis
              </CardTitle>
              <div className="flex items-center gap-2">
                {paymentStatusBadge(quote.payment_status)}
                {signatureStatusBadge(quote.signature_status)}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="overflow-x-auto border rounded">
              <table className="w-full text-sm">
                <tbody>
                  {(quote.result?.lines || []).map((line, i) => (
                    <tr key={i} className={`border-t ${line.label === 'MONTANT GLOBAL' ? 'font-bold bg-blue-50' : ''}`}>
                      <td className="px-3 py-2">{line.label}</td>
                      <td className="px-3 py-2 text-right">{fmtEUR(line.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {quote.include_gfa && (
              <p className="text-xs text-emerald-700">
                <CheckCircle2 className="h-3 w-3 inline mr-1" />
                Option GFA (Garantie Financière d&apos;Achèvement) incluse
              </p>
            )}
            {quote.include_legal_review && (
              <p className="text-xs text-blue-700">
                <ShieldCheck className="h-3 w-3 inline mr-1" />
                Forfait Revue de conformité CCMI inclus
              </p>
            )}
          </CardContent>
        </Card>

        {/* Bloc paiement */}
        {!isPaid && (
          <Card className="border-blue-200" data-testid="payment-card">
            <CardHeader className="bg-blue-50 rounded-t-lg">
              <CardTitle className="text-base flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-blue-700" />
                Choisir un mode de paiement
              </CardTitle>
              <CardDescription>
                Montant total à régler : <strong>{fmtEUR(quote.amount_total_ttc)}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              {/* Champs pour SEPA 3x */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="customer_email">Email constructeur</Label>
                  <Input
                    id="customer_email" type="email" value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="constructeur@…"
                    data-testid="customer-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="customer_name">Nom du constructeur (optionnel)</Label>
                  <Input
                    id="customer_name" value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    data-testid="customer-name-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Button
                  onClick={() => initPayment('cb')}
                  disabled={initing}
                  className="bg-blue-700 hover:bg-blue-800 text-white h-auto py-3 flex-col"
                  data-testid="pay-cb-btn"
                >
                  <CreditCard className="h-5 w-5 mb-1" />
                  <span>Carte bancaire</span>
                  <span className="text-xs opacity-80">Stripe · 100%</span>
                </Button>
                <Button
                  onClick={() => initPayment('virement')}
                  disabled={initing}
                  variant="outline"
                  className="h-auto py-3 flex-col"
                  data-testid="pay-virement-btn"
                >
                  <Landmark className="h-5 w-5 mb-1" />
                  <span>Virement bancaire</span>
                  <span className="text-xs opacity-70">IBAN O&apos;SCOP · 100%</span>
                </Button>
                <Button
                  onClick={() => initPayment('sepa_3x')}
                  disabled={initing || !customerEmail}
                  variant="outline"
                  className="h-auto py-3 flex-col border-emerald-300"
                  data-testid="pay-sepa3x-btn"
                >
                  <Repeat className="h-5 w-5 mb-1 text-emerald-700" />
                  <span>SEPA 3 fois</span>
                  <span className="text-xs opacity-70">40% / 30% / 30%</span>
                </Button>
              </div>

              {/* Affichage info virement */}
              {virementInfo && (
                <Card className="bg-amber-50 border-amber-200 mt-3" data-testid="virement-info">
                  <CardContent className="pt-4 space-y-2 text-sm">
                    <p className="font-semibold text-amber-900">
                      📋 Effectuez votre virement avec ces coordonnées :
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      <div><span className="text-slate-500">Bénéficiaire :</span> <strong>{virementInfo.beneficiary}</strong></div>
                      <div><span className="text-slate-500">Banque :</span> {virementInfo.bank}</div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">IBAN :</span>
                        <strong className="font-mono">{virementInfo.iban}</strong>
                        <Button size="sm" variant="ghost" onClick={() => copyText(virementInfo.iban)}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      <div><span className="text-slate-500">BIC :</span> <strong className="font-mono">{virementInfo.bic}</strong></div>
                      <div className="md:col-span-2 flex items-center gap-2">
                        <span className="text-slate-500">Référence à indiquer :</span>
                        <strong className="font-mono text-red-700">{virementInfo.reference}</strong>
                        <Button size="sm" variant="ghost" onClick={() => copyText(virementInfo.reference)}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      <div><span className="text-slate-500">Montant :</span> <strong>{fmtEUR(virementInfo.amount_total_ttc)}</strong></div>
                    </div>
                    <p className="text-xs text-amber-700 mt-2">{virementInfo.instructions}</p>
                  </CardContent>
                </Card>
              )}

              {quote.payment_method === 'sepa_3x' && quote.sepa_schedule && (
                <Card className="bg-emerald-50 border-emerald-200 mt-3" data-testid="sepa-schedule-info">
                  <CardContent className="pt-4 space-y-2 text-sm">
                    <p className="font-semibold text-emerald-900">Planning SEPA 3x :</p>
                    <ul className="space-y-1 text-xs">
                      {quote.sepa_schedule.map((step) => (
                        <li key={step.index} className="flex justify-between">
                          <span>Échéance {step.index}/3 — {step.charge_date}</span>
                          <span className="font-mono">
                            {fmtEUR(step.amount_cents / 100)} · {step.status === 'confirmed' ? '✅' : step.status === 'failed' ? '❌' : '⏳'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        )}

        {/* Bloc signature */}
        {paymentInitiated && !isSigned && (
          <Card className="border-emerald-300" data-testid="signature-card">
            <CardHeader className="bg-emerald-50 rounded-t-lg">
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-700" />
                Signature électronique
              </CardTitle>
              <CardDescription>
                {isPaid
                  ? 'Paiement validé. Finalisez votre devis en signant par OTP.'
                  : 'Votre paiement est initié. Vous pouvez signer dès maintenant.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <div className="flex gap-2">
                <Button
                  variant={otpChannel === 'email' ? 'default' : 'outline'}
                  size="sm" onClick={() => setOtpChannel('email')}
                  data-testid="otp-channel-email"
                >
                  <Mail className="h-4 w-4 mr-1" /> Email
                </Button>
                <Button
                  variant={otpChannel === 'sms' ? 'default' : 'outline'}
                  size="sm" onClick={() => setOtpChannel('sms')}
                  data-testid="otp-channel-sms"
                >
                  <MessageSquare className="h-4 w-4 mr-1" /> SMS
                </Button>
              </div>
              <div>
                <Label htmlFor="otp_contact">
                  {otpChannel === 'sms' ? 'Téléphone (format E.164, ex: +33612345678)' : 'Email'}
                </Label>
                <Input
                  id="otp_contact"
                  type={otpChannel === 'sms' ? 'tel' : 'email'}
                  value={otpContact}
                  onChange={(e) => setOtpContact(e.target.value)}
                  placeholder={otpChannel === 'sms' ? '+33612345678' : 'vous@constructeur.fr'}
                  data-testid="otp-contact-input"
                />
              </div>
              <Button
                onClick={requestOtp}
                disabled={otpSending || !otpContact}
                className="bg-emerald-700 hover:bg-emerald-800 text-white w-full"
                data-testid="otp-request-btn"
              >
                {otpSending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Clock className="h-4 w-4 mr-1" />}
                Envoyer le code
              </Button>

              {otpRequested && (
                <div className="space-y-2 mt-3 border-t pt-3">
                  <Label htmlFor="otp_code">Code reçu (6 chiffres)</Label>
                  <Input
                    id="otp_code" inputMode="numeric" maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456"
                    className="font-mono text-center text-2xl tracking-widest"
                    data-testid="otp-code-input"
                  />
                  <Button
                    onClick={verifyOtp}
                    disabled={otpVerifying || otpCode.length !== 6}
                    className="bg-emerald-800 hover:bg-emerald-900 text-white w-full"
                    data-testid="otp-verify-btn"
                  >
                    {otpVerifying ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                    Valider et signer
                  </Button>
                  {otpRequestedAt && (
                    <p className="text-xs text-slate-500 text-center">
                      Code valable jusqu&apos;au {new Date(otpRequestedAt).toLocaleString('fr-FR')}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* État final */}
        {isSigned && (
          <Card className="border-emerald-400 bg-emerald-50" data-testid="signed-card">
            <CardContent className="pt-6 text-center space-y-3">
              <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
              <h2 className="text-xl font-bold text-emerald-900">Devis signé !</h2>
              <p className="text-sm text-emerald-800">
                Signé le {new Date(quote.signed_at).toLocaleString('fr-FR')}<br/>
                via {quote.signature_channel === 'sms' ? 'SMS' : 'Email'}
              </p>
              {quote.signed_document_reference && (
                <div className="space-y-2 pt-2 border-t border-emerald-300">
                  <p className="text-xs text-emerald-700">
                    Document officiel : <span className="font-mono">{quote.signed_document_reference}</span>
                  </p>
                  <Button
                    onClick={() => window.open(`${API}/public/quote/${token}/signed-pdf`, '_blank')}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                    data-testid="download-signed-pdf-btn"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Télécharger le PDF officiel (avec QR de vérification)
                  </Button>
                  {quote.signed_verification_url && (
                    <p className="text-xs text-emerald-600">
                      Vérification publique : <a href={quote.signed_verification_url} target="_blank" rel="noreferrer" className="underline">{quote.signed_verification_url}</a>
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-slate-400 mt-4">
          COPPAM OSCOP · Devis sécurisé par token unique
        </p>
      </div>
    </div>
  );
}
